import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer, 
  Legend,
  Tooltip
} from 'recharts';
import styles from './MultiMetricRadarChart.module.css';

const MultiMetricRadarChart = ({ 
  metrics = [], 
  title = "Performance Radar",
  className = "",
  showLegend = true,
  showTooltip = true 
}) => {
  const [hoveredSpoke, setHoveredSpoke] = useState(null);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  // Transform metrics data for RadarChart
  const transformedData = metrics.map(metric => {
    // Normalize values to 0-100 scale for better visualization
    const normalizeValue = (value, min, max, metricName) => {
      if (max === min) return 50; // Default to middle if no range
      
      // For GPA, use fixed 1.0-4.0 scale instead of percentile range
      if (metricName === 'GPA') {
        const gpaMin = 1.0;
        const gpaMax = 4.0;
        return Math.max(0, Math.min(100, ((value - gpaMin) / (gpaMax - gpaMin)) * 100));
      }
      
      // For SAT, use fixed 400-1600 scale instead of percentile range
      if (metricName === 'SAT') {
        const satMin = 400;
        const satMax = 1600;
        return Math.max(0, Math.min(100, ((value - satMin) / (satMax - satMin)) * 100));
      }
      
      // For ACT, use fixed 1-36 scale instead of percentile range
      if (metricName === 'ACT') {
        const actMin = 1;
        const actMax = 36;
        return Math.max(0, Math.min(100, ((value - actMin) / (actMax - actMin)) * 100));
      }
      
      // For Class Rank, use fixed 1-100 scale (1 = top 1%, 100 = bottom 50%)
      if (metricName === 'Class Rank') {
        const rankMin = 1;
        const rankMax = 100;
        return Math.max(0, Math.min(100, ((value - rankMin) / (rankMax - rankMin)) * 100));
      }
      
      // For other metrics, use the provided min/max range
      return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
    };

    // For GPA, SAT, and ACT, we need to normalize the percentile range against their fixed scales
    let min25th, max75th;
    if (metric.name === 'GPA') {
      const gpaMin = 1.0;
      const gpaMax = 4.0;
      min25th = normalizeValue(metric.min, gpaMin, gpaMax, 'GPA');
      max75th = normalizeValue(metric.max, gpaMin, gpaMax, 'GPA');
    } else if (metric.name === 'SAT') {
      const satMin = 400;
      const satMax = 1600;
      min25th = normalizeValue(metric.min, satMin, satMax, 'SAT');
      max75th = normalizeValue(metric.max, satMin, satMax, 'SAT');
    } else if (metric.name === 'ACT') {
      const actMin = 1;
      const actMax = 36;
      min25th = normalizeValue(metric.min, actMin, actMax, 'ACT');
      max75th = normalizeValue(metric.max, actMin, actMax, 'ACT');
    } else if (metric.name === 'Class Rank') {
      const rankMin = 1;
      const rankMax = 100;
      min25th = normalizeValue(metric.min, rankMin, rankMax, 'Class Rank');
      max75th = normalizeValue(metric.max, rankMin, rankMax, 'Class Rank');
    } else {
      min25th = normalizeValue(metric.min, metric.min, metric.max, metric.name);
      max75th = normalizeValue(metric.max, metric.min, metric.max, metric.name);
    }

    return {
      subject: metric.name,
      userValue: normalizeValue(metric.userValue, metric.min, metric.max, metric.name),
      min25th: min25th,
      max75th: max75th,
      rawUserValue: metric.userValue,
      rawMin: metric.min,
      rawMax: metric.max,
      fullMark: 100
    };
  });

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      if (!data) return null;

      return (
        <motion.div 
          className={styles.customTooltip}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <h4 className={styles.tooltipTitle}>{label}</h4>
          <div className={styles.tooltipContent}>
            <div className={styles.tooltipItem}>
              <span className={styles.tooltipLabel}>Your Score:</span>
              <span className={styles.tooltipValue}>{data.rawUserValue}</span>
            </div>
            <div className={styles.tooltipItem}>
              <span className={styles.tooltipLabel}>25th Percentile:</span>
              <span className={styles.tooltipValue}>{data.rawMin}</span>
            </div>
            <div className={styles.tooltipItem}>
              <span className={styles.tooltipLabel}>75th Percentile:</span>
              <span className={styles.tooltipValue}>{data.rawMax}</span>
            </div>
            {label === 'GPA' && (
              <div className={styles.tooltipItem}>
                <span className={styles.tooltipLabel}>Scale:</span>
                <span className={styles.tooltipValue}>1.0 - 4.0</span>
              </div>
            )}
            {label === 'SAT' && (
              <div className={styles.tooltipItem}>
                <span className={styles.tooltipLabel}>Scale:</span>
                <span className={styles.tooltipValue}>400 - 1600</span>
              </div>
            )}
            {label === 'ACT' && (
              <div className={styles.tooltipItem}>
                <span className={styles.tooltipLabel}>Scale:</span>
                <span className={styles.tooltipValue}>1 - 36</span>
              </div>
            )}
            {label === 'Class Rank' && (
              <div className={styles.tooltipItem}>
                <span className={styles.tooltipLabel}>Scale:</span>
                <span className={styles.tooltipValue}>Top 1% - Bottom 50%</span>
              </div>
            )}
          </div>
        </motion.div>
      );
    }
    return null;
  };

  // Custom legend component
  const CustomLegend = ({ payload }) => (
    <motion.div 
      className={styles.legendContainer}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1, duration: 0.5 }}
    >
      {payload.map((entry, index) => (
        <div key={index} className={styles.legendItem}>
          <div 
            className={styles.legendColor} 
            style={{ backgroundColor: entry.color }}
          />
          <span className={styles.legendText}>{entry.value}</span>
        </div>
      ))}
    </motion.div>
  );

  // Animation variants
  const chartVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  if (!metrics || metrics.length === 0) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.emptyState}>
          <p>No metrics data available</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className={`${styles.container} ${className}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {title && (
        <motion.h3 
          className={styles.title}
          variants={{
            hidden: { opacity: 0, y: -10 },
            visible: { opacity: 1, y: 0 }
          }}
        >
          {title}
        </motion.h3>
      )}
      
      <motion.div 
        className={styles.chartWrapper}
        variants={chartVariants}
        onAnimationComplete={() => setIsAnimationComplete(true)}
      >
        <ResponsiveContainer width="100%" height={400}>
          <RadarChart 
            data={transformedData}
            margin={{ top: 20, right: 30, bottom: 20, left: 30 }}
          >
            <PolarGrid 
              stroke="#E0E0E0" 
              strokeWidth={1}
              gridType="polygon"
            />
            
            <PolarAngleAxis 
              dataKey="subject" 
              tick={{ 
                fontSize: 12, 
                fill: '#666666',
                fontWeight: 500
              }}
              className={styles.angleAxis}
            />
            
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]} 
              tick={{ 
                fontSize: 10, 
                fill: '#999999' 
              }}
              tickCount={5}
              axisLine={false}
            />

            {/* College percentile range (background) */}
            <Radar
              name="College Range (25th-75th)"
              dataKey="max75th"
              stroke="#E3F2FD"
              fill="#E3F2FD"
              fillOpacity={0.3}
              strokeWidth={2}
              dot={false}
              animationDuration={isAnimationComplete ? 0 : 1500}
              animationDelay={300}
            />

            {/* User performance line */}
            <Radar
              name="Your Performance"
              dataKey="userValue"
              stroke="#2196F3"
              fill="#2196F3"
              fillOpacity={0.1}
              strokeWidth={3}
              dot={{ 
                fill: '#2196F3', 
                strokeWidth: 2, 
                stroke: '#ffffff',
                r: 4 
              }}
              activeDot={{ 
                r: 6, 
                stroke: '#2196F3', 
                strokeWidth: 2,
                fill: '#ffffff'
              }}
              animationDuration={isAnimationComplete ? 0 : 2000}
              animationDelay={800}
              onMouseEnter={(data, index) => setHoveredSpoke(index)}
              onMouseLeave={() => setHoveredSpoke(null)}
            />

            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            {showLegend && <Legend content={<CustomLegend />} />}
          </RadarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Remove Performance Summary section below */}
      {/*
      <motion.div 
        className={styles.summaryContainer}
        variants={{
          hidden: { opacity: 0, y: 10 },
          visible: { opacity: 1, y: 0 }
        }}
        transition={{ delay: 1.5 }}
      >
        <div className={styles.summaryGrid}>
          {transformedData.map((metric, index) => (
            <motion.div 
              key={metric.subject}
              className={`${styles.summaryItem} ${hoveredSpoke === index ? styles.highlighted : ''}`}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className={styles.summaryLabel}>{metric.subject}</div>
              <div className={styles.summaryValueNew}>
                <div className={styles.scoreBlock}>
                  <span className={styles.userScore}>{metric.rawUserValue}</span>
                  <span className={styles.userLabel}>Your Score</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
      */}
    </motion.div>
  );
};

export default MultiMetricRadarChart; 